import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getStaffProfiles,
  createStaffProfile,
  updateStaffProfile,
  deleteStaffProfile,
} from "../../../services/StaffProfileService";
import { getUsers } from "../../../services/UserService";
import { getRoles } from "../../../services/RoleService";
import DataTablePagination from "../common/DataTablePagination";
import { useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const emptyForm = {
  accountMode: "new", // "new" = créer le compte en même temps | "existing" = attacher un compte déjà créé
  user_id: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  phone: "",
  nni: "",
  gender: "",
  date_of_birth: "",
  place_of_birth: "",
  nationality: "",
  role_ids: [],
  matricule: "",
  structure: "",
  sub_structure: "",
  position: "",
  category: "",
  hire_date: "",
  reference_act: "",
  is_active: true,
};

const StaffProfiles = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.STAFF_PROFILE_CREATE);
  const canUpdate = can(PERMISSIONS.STAFF_PROFILE_UPDATE);
  const canDelete = can(PERMISSIONS.STAFF_PROFILE_DELETE);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [structureFilter, setStructureFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
    getRoles({ page: 1, limit: 200 }).then((res) => setRoles(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (structureFilter) params.structure = structureFilter;

      const res = await getStaffProfiles(params);
      const body = res.data;
      setProfiles(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.staffProfiles.impossibleChargerPersonnelAdministratifTechnique"));
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, structureFilter, t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const userLabel = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return userId;
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name ? `${name} (${user.email})` : user.email;
  };

  const usersWithoutProfile = users.filter((u) => !profiles.some((p) => p.user_id === u.id));

  const visibleProfiles = search
    ? profiles.filter((p) => userLabel(p.user_id).toLowerCase().includes(search.toLowerCase()))
    : profiles;

  const structures = Array.from(new Set(profiles.map((p) => p.structure).filter(Boolean)));

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStructureFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (profile) => {
    setFormMode("edit");
    setFormData({
      ...emptyForm,
      user_id: profile.user_id || "",
      matricule: profile.matricule || "",
      structure: profile.structure || "",
      sub_structure: profile.sub_structure || "",
      position: profile.position || "",
      category: profile.category || "",
      hire_date: profile.hire_date ? profile.hire_date.substring(0, 10) : "",
      reference_act: profile.reference_act || "",
      is_active: !!profile.is_active,
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(profile.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleRole = (roleId) => {
    setFormData((prev) => {
      const has = prev.role_ids.includes(roleId);
      return { ...prev, role_ids: has ? prev.role_ids.filter((id) => id !== roleId) : [...prev.role_ids, roleId] };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (formMode === "add") {
      if (formData.accountMode === "existing") {
        if (!formData.user_id) errors.user_id = t("common.ui.leCompteUtilisateurEstRequis");
      } else {
        if (!formData.first_name.trim()) errors.first_name = t("common.ui.lePrenomEstRequis");
        if (!formData.last_name.trim()) errors.last_name = t("common.ui.leNomEstRequis");
        if (!formData.email.trim()) errors.email = t("common.ui.lEmailEstRequis");
        if (!formData.password || formData.password.length < 6)
          errors.password = t("common.ui.leMotPasseDoitContenirMoins");
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const staffFieldsPayload = () => ({
    matricule: formData.matricule.trim() || null,
    structure: formData.structure.trim() || null,
    sub_structure: formData.sub_structure.trim() || null,
    position: formData.position.trim() || null,
    category: formData.category.trim() || null,
    hire_date: formData.hire_date || null,
    reference_act: formData.reference_act.trim() || null,
    is_active: formData.is_active,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      if (formMode === "add") {
        const payload =
          formData.accountMode === "existing"
            ? { user_id: formData.user_id }
            : {
                email: formData.email.trim(),
                password: formData.password,
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                phone: formData.phone.trim() || null,
                nni: formData.nni.trim() || null,
                gender: formData.gender || null,
                date_of_birth: formData.date_of_birth || null,
                place_of_birth: formData.place_of_birth.trim() || null,
                nationality: formData.nationality.trim() || null,
                role_ids: formData.role_ids,
              };
        await createStaffProfile({ ...payload, ...staffFieldsPayload() });
        setFeedback({
          type: "success",
          message:
            formData.accountMode === "existing"
              ? t("pages.staffProfiles.profilCreeAvecSucces")
              : t("pages.staffProfiles.compteProfilCreesAvecSucces"),
        });
      } else {
        await updateStaffProfile(editingId, staffFieldsPayload());
        setFeedback({ type: "success", message: t("pages.staffProfiles.profilMisJour") });
      }
      setShowFormModal(false);
      fetchProfiles();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteStaffProfile(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.staffProfiles.profilSupprime") });
      setDeleteTarget(null);
      if (profiles.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchProfiles();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("common.ui.impossibleSupprimerProfil") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("pages.staffProfiles.personnelAdministratifTechnique")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" title={t("common.crud.refresh")} onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.staffProfiles.ajouterProfil")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("pages.staffProfiles.listePersonnel")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("common.ui.rechercherNomEmail")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={structureFilter}
                  onChange={(e) => {
                    setStructureFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("pages.staffProfiles.toutesStructures")}</option>
                  {structures.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <div className="table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.fields.name")}</th>
                    <th>{t("common.fields.matricule")}</th>
                    <th>{t("pages.staffProfiles.structure")}</th>
                    <th>{t("pages.staffProfiles.poste")}</th>
                    <th>{t("common.ui.categorie")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : visibleProfiles.length > 0 ? (
                    visibleProfiles.map((profile) => (
                      <tr key={profile.id}>
                        <td className="fw-medium">{userLabel(profile.user_id)}</td>
                        <td>{profile.matricule || "-"}</td>
                        <td>
                          {profile.structure || "-"}
                          {profile.sub_structure ? ` / ${profile.sub_structure}` : ""}
                        </td>
                        <td>{profile.position || "-"}</td>
                        <td>{profile.category || "-"}</td>
                        <td>
                          <span className={`badge badge-soft-${profile.is_active ? "success" : "danger"} d-inline-flex align-items-center`}>
                            <i className="ti ti-circle-filled fs-5 me-1"></i>
                            {profile.is_active ? t("common.crud.statusActive") : t("common.crud.statusInactive")}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(profile)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(profile)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.staffProfiles.aucunProfilTrouve")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <DataTablePagination
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.staffProfiles.ajouterProfil") : t("pages.staffProfiles.modifierProfil")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            {formMode === "add" && (
              <>
                <div className="mb-3">
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "new" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "new")}
                    >
                      {t("common.crud.newAccount")}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "existing" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "existing")}
                    >
                      {t("common.crud.existingAccount")}
                    </button>
                  </div>
                </div>

                {formData.accountMode === "new" ? (
                  <>
                    <div className="row">
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">
                          {t("common.fields.firstName")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${formErrors.first_name ? "is-invalid" : ""}`}
                          value={formData.first_name}
                          onChange={(e) => handleFormChange("first_name", e.target.value)}
                        />
                        {formErrors.first_name && <div className="invalid-feedback d-block">{formErrors.first_name}</div>}
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">
                          {t("common.fields.name")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${formErrors.last_name ? "is-invalid" : ""}`}
                          value={formData.last_name}
                          onChange={(e) => handleFormChange("last_name", e.target.value)}
                        />
                        {formErrors.last_name && <div className="invalid-feedback d-block">{formErrors.last_name}</div>}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.fields.email")} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                        value={formData.email}
                        onChange={(e) => handleFormChange("email", e.target.value)}
                      />
                      {formErrors.email && <div className="invalid-feedback d-block">{formErrors.email}</div>}
                    </div>
                    <div className="row">
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">
                          {t("common.fields.password")} <span className="text-danger">*</span>
                        </label>
                        <input
                          type="password"
                          className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                          value={formData.password}
                          onChange={(e) => handleFormChange("password", e.target.value)}
                        />
                        {formErrors.password && <div className="invalid-feedback d-block">{formErrors.password}</div>}
                      </div>
                      <div className="col-sm-6 mb-3">
                        <label className="form-label">{t("common.fields.phone")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => handleFormChange("phone", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.sexe")}</label>
                        <select
                          className="form-select"
                          value={formData.gender}
                          onChange={(e) => handleFormChange("gender", e.target.value)}
                        >
                          <option value="">-</option>
                          <option value="M">{t("common.ui.masculin")}</option>
                          <option value="F">{t("common.ui.feminin")}</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.dateNaissance")}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date_of_birth}
                          onChange={(e) => handleFormChange("date_of_birth", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.lieuNaissance")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.place_of_birth}
                          onChange={(e) => handleFormChange("place_of_birth", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nniNumeroNationalIdentite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.nni}
                          onChange={(e) => handleFormChange("nni", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nationalite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("common.ui.exMauritanienne")}
                          value={formData.nationality}
                          onChange={(e) => handleFormChange("nationality", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">{t("common.fields.roles")}</label>
                      <div className="border rounded p-2" style={{ maxHeight: "140px", overflowY: "auto" }}>
                        {roles.map((role) => (
                          <div className="form-check" key={role.id}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`role-${role.id}`}
                              checked={formData.role_ids.includes(role.id)}
                              onChange={() => toggleRole(role.id)}
                            />
                            <label className="form-check-label" htmlFor={`role-${role.id}`}>
                              {role.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">
                      {t("pages.staffProfiles.compteUtilisateur")} <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.user_id ? "is-invalid" : ""}`}
                      value={formData.user_id}
                      onChange={(e) => handleFormChange("user_id", e.target.value)}
                    >
                      <option value="">{t("common.crud.selectPlaceholder")}</option>
                      {usersWithoutProfile.map((u) => (
                        <option key={u.id} value={u.id}>
                          {userLabel(u.id)}
                        </option>
                      ))}
                    </select>
                    {formErrors.user_id && <div className="invalid-feedback d-block">{formErrors.user_id}</div>}
                  </div>
                )}
              </>
            )}

            <hr />
            <h6 className="fw-bold mb-2">{t("common.ui.informationsRh")}</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.fields.matricule")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.matricule}
                  onChange={(e) => handleFormChange("matricule", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("common.ui.categorie")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.staffProfiles.exPtasAdministrateurTechnologue")}
                  value={formData.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.staffProfiles.structure")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.staffProfiles.exDirectionGenerale")}
                  value={formData.structure}
                  onChange={(e) => handleFormChange("structure", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.staffProfiles.sousStructure")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.staffProfiles.exSecretariatGeneral")}
                  value={formData.sub_structure}
                  onChange={(e) => handleFormChange("sub_structure", e.target.value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.staffProfiles.poste")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.staffProfiles.exChefServiceDirecteur")}
                  value={formData.position}
                  onChange={(e) => handleFormChange("position", e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.staffProfiles.dateRecrutement")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.hire_date}
                  onChange={(e) => handleFormChange("hire_date", e.target.value)}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("pages.staffProfiles.referenceActeNomination")}</label>
              <input
                type="text"
                className="form-control"
                placeholder={t("pages.staffProfiles.exConseilMinistresLettreN234")}
                value={formData.reference_act}
                onChange={(e) => handleFormChange("reference_act", e.target.value)}
              />
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="staff-active"
                checked={formData.is_active}
                onChange={(e) => handleFormChange("is_active", e.target.checked)}
              />
              <label className="form-check-label" htmlFor="staff-active">
                {t("pages.staffProfiles.profilActif")}
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>
            {t("pages.staffProfiles.voulezVousVraimentSupprimerProfil")} <strong>{deleteTarget ? userLabel(deleteTarget.user_id) : ""}</strong> ?
          </p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffProfiles;
